<template>
  <a-cascader
    v-model:value="modalRef.selectedRows"
    size="small"
    style="min-width: 150px"
    :placeholder="placeholder"
    :disabled="field.disabled"
    :default-value="field.defaultValue"
    :allow-clear="field.allowClear"
    :options="modalRef.datasource"
    :multiple="multiple"
    :show-search="show_search"
    :field-names="{ label: 'title', value: 'value', children: 'children' }"
    :maxTagCount="field.maxTagCount"
    :dropdownStyle="{ maxHeight: '400px' }"
    :expandTrigger="field.expandTrigger ? field.expandTrigger : 'click'"
    :changeOnSelect="field.changeOnSelect ? field.changeOnSelect : false"
    @change="handleChange"
    @mouseenter="handleMouseenter"
  />
</template>

<script lang="ts">
import { defineComponent, reactive, watch, onMounted, onUnmounted } from 'vue';
import type { ReponseData } from 'liyu-pc-base/utils/hooks/useFetchData';
import { convertValueByClassName, isNotEmpty } from 'liyu-pc-base/utils/function';
import { debounce, isArray } from 'lodash-es';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useRequiredConditionStore, required_condition_event_obj } from 'liyu-pc-base/init-plugin';

export default defineComponent({
  name: 'RemoteCascaderPro',
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [Array, Number, String],
      required: false,
      default: () => null,
    },
    valueKey: {
      type: String,
    },
    labelKeyType: {
      type: Number,
      default: () => 1,
    },
    labelKey: {
      type: String,
    },
    labelSpace: {
      type: String,
      default: '-',
    },
    labelKeyList: {
      type: Array,
    },
    search_config: {
      type: Object,
      required: false,
      default: () => {},
    },
    label: {
      type: [Array, String],
      required: false,
    },
    formModel: {
      type: Object,
      required: true,
    },
    outModel: {
      type: Object,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: () => false,
    },
    show_search: {
      type: Boolean,
      default: () => false,
    },
    placeholder: {
      type: String,
    },
    init_model: {
      type: Object,
      default: () => {},
    },
    init_watch: {
      // 普通查询监听字段 {监听key: 传参key}
      type: Object,
      default: () => {},
    },
    is_form_design: {
      type: Boolean,
      default: () => false,
    },
  },
  emits: ['update:value', 'update:label'],
  setup(props, { emit }) {
    const requiredConditionStore = useRequiredConditionStore();
    const modalRef: any = reactive({
      selectedRows: props.field.multiple ? [] : null,
      datasource: [],
      // baseData: null,
    });
    let is_refresh = false; // 是否触发刷新
    const getSelectedRows = (value: any) => {
      modalRef.selectedRows = [];
      if (isArray(value)) {
        if (props.field.multiple) {
          if (value.length > 0) {
            if (isArray(value[0])) {
              value.forEach((item: any) => {
                modalRef.selectedRows.push(item);
              });
            } else {
              modalRef.selectedRows = [value];
            }
          }
        } else {
          if (value.length > 0) {
            if (isArray(value[0])) {
              modalRef.selectedRows = value[0];
            } else {
              modalRef.selectedRows = value;
            }
          }
        }
      }
    };
    getSelectedRows(props.value);
    const getTreeData = (res: any) => {
      const result = [...res.data];
      // 递归排序函数
      function dataChildren(nodes) {
        nodes.forEach(node => {
          node.key = node[get_value_key()];
          node.value = node[get_value_key()];
          if (props.labelKeyList && props.labelKeyList.length > 0 && props.labelKeyType == 2) {
            const label = [];
            for (const key of props.labelKeyList) {
              label.push(node[key as string]);
            }
            node.title = label.join(props.labelSpace);
          } else {
            node.title = node[get_label_key()];
          }
          if (node?.children && node.children.length > 0) {
            dataChildren(node.children); // 递归排序子节点
          } else {
            delete node.children;
          }
        });
      }
      dataChildren(result); // 然后递归地对每个根节点的子节点排序
      modalRef.datasource = result;
    };
    const get_value_key = () => {
      return props.valueKey ? (props.valueKey as string) : 'id';
    };
    const get_label_key = () => {
      return props.labelKey ? (props.labelKey as string) : 'name';
    };
    const getConditionsItem = (value: any, list: any, item: any) => {
      if (item.operator === 'not_blank_not_null') {
        list.push({
          field_name: item.field_name,
          operator: item.operator,
          value: [],
          format: item.format,
          class_name: item.class_name,
        });
      } else if (value instanceof Array) {
        const [first] = value;
        if (isNotEmpty(first)) {
          list.push({
            field_name: item.field_name,
            operator: item.operator,
            value: convertValueByClassName(value, item.class_name),
            format: item.format,
            class_name: item.class_name,
          });
        }
      } else if (isNotEmpty(value)) {
        let val = value;
        if (item.operator === 'in' || item.operator === 'notin') {
          if (value.includes('[')) {
            val = JSON.parse(value);
          } else if (value.includes(',')) {
            val = value.split(',');
          }
          if (item.class_name === 'int') {
            val = val.map(Number);
          }
        }
        list.push({
          field_name: item.field_name,
          operator: item.operator,
          value: convertValueByClassName(val instanceof Array ? val : [val], item.class_name),
          format: item.format,
          class_name: item.class_name,
        });
      }
    };
    /**
     * searchType === 1 时按 key 同时处理 outModel 与 formModel：
     * outModel 中该字段有值（非 undefined / null）时优先，否则回退 formModel 的同名字段。
     * 不能用 `outModel ? outModel : formModel` 做对象级二选一（outModel 默认为 {} 恒为真值）。
     */
    const resolveModelValue = (selectName: any): { found: boolean; value: any } => {
      if (
        props.outModel &&
        Object.prototype.hasOwnProperty.call(props.outModel, selectName) &&
        props.outModel[selectName] !== undefined &&
        props.outModel[selectName] !== null
      ) {
        return { found: true, value: props.outModel[selectName] };
      }
      if (props.formModel && Object.prototype.hasOwnProperty.call(props.formModel, selectName)) {
        return { found: true, value: props.formModel[selectName] };
      }
      return { found: false, value: undefined };
    };
    const getConditions = () => {
      const conditions = [];
      props.field.searchConditions.forEach((item: any) => {
        const list = [];
        item.forEach((item1: any) => {
          if (item1.searchType === 1) {
            // outModel 优先、formModel 兜底，两者同时参与查找
            const resolved = resolveModelValue(item1.select_name);
            if (resolved.found) {
              getConditionsItem(resolved.value, list, item1);
            }
          } else if (item1.searchType === 2) {
            Object.keys(props.formModel).forEach((key: any) => {
              if (item1.select_name == key) {
                getConditionsItem(props.formModel[key], list, item1);
              }
            });
          } else {
            getConditionsItem(item1.value, list, item1);
          }
        });
        conditions.push(list);
      });
      return conditions;
    };
    const getSimpleSearchParams = () => {
      const params = {};
      if (props.field.simpleSearchConditions) {
        props.field.simpleSearchConditions.forEach((item: any) => {
          if (item.searchType === 1) {
            // outModel 优先、formModel 兜底，两者同时参与查找
            const resolved = resolveModelValue(item.select_name);
            if (resolved.found) {
              params[item.field_name] = resolved.value;
            }
          } else if (item.searchType === 3) {
            params[item.field_name] = item.value;
          }
        });
        return params;
      }
    };
    const searchConfig: any = {
      current: 1,
      pageSize: 2000,
      is_pagination: true,
      is_active: true,
      ...props.init_model, // 普通查询条件
      column_fields: [],
      conditions: [],
      simpleSearchParams: {},
    };
    if (props.field.queryType === 'simple') {
      searchConfig.simpleSearchParams = getSimpleSearchParams();
    } else {
      searchConfig.conditions = getConditions();
    }
    const getColumnFields = () => {
      const list = [];
      if (props.labelKeyType == 2) {
        list.push(get_value_key(), ...props.labelKeyList);
      } else {
        list.push(get_value_key(), get_label_key());
      }
      searchConfig.column_fields = [].concat(list);
    };
    getColumnFields();
    const getLabelFromDatasource = (datasource: Array<any>) => {
      const labelList: Array<string> = [];
      datasource.forEach((item: any) => {
        if (modalRef.selectedRows instanceof Array && modalRef.selectedRows.includes(item.key)) {
          labelList.push(item.title);
        }
        if (!(modalRef.selectedRows instanceof Array) && modalRef.selectedRows == item.key) {
          labelList.push(item.title);
        }
        if (item.children && item.children.length > 0) {
          getLabelFromDatasource(item.children).forEach(obj => labelList.push(obj));
        }
      });
      return labelList;
    };
    const handleChange = () => {
      emit('update:value', modalRef.selectedRows);
      emit('update:label', getLabelFromDatasource(modalRef.datasource));
    };

    watch(
      () => props.formModel[props.field.name],
      () => {
        getSelectedRows(props.formModel[props.field.name]);
        emit('update:label', getLabelFromDatasource(modalRef.datasource));
      },
    );
    const getRequiredConditionObj = () => {
      const required_condition = props.field.required_condition;
      const required_condition_obj = {};
      if (required_condition) {
        required_condition.forEach((item: any) => {
          const value = requiredConditionStore.value_map[item.key];
          if (value !== undefined && value !== null) {
            required_condition_obj[item.field_name] = value;
          }
        });
      }
      return required_condition_obj;
    };
    const refresh = () => {
      let params: any = {};
      if (props.field.queryType === 'simple') {
        params = {
          ...searchConfig.simpleSearchParams,
          required_condition: getRequiredConditionObj(),
        };
      } else {
        params = {
          ...searchConfig,
          ...props.search_config,
          required_condition: getRequiredConditionObj(),
        };
        delete params.simpleSearchParams;
      }
      if (props.field.postUrl) {
        getApiData(
          params,
          props.field.postUrl.replace('__tree', ''),
          props.field.postUrl.includes('__tree'),
        ).then(function (res: ReponseData<any>) {
          modalRef.baseData = res;
          getTreeData(modalRef.baseData);
          console;
          if (!props.is_form_design) {
            if (props.field.defaultValueType === 'first') {
              const result = [];
              getFirstData(modalRef.datasource, result);
              modalRef.selectedRows = result;
            } else if (props.field.defaultValueType === 'global') {
              if (!modalRef.selectedRows || modalRef.selectedRows.length === 0) {
                const globalDefaultKey = props.field.globalDefaultKey;
                const globalDefaultField = props.field.globalDefaultField;
                const globalDefaultValue = requiredConditionStore.value_map[globalDefaultKey];
                if (globalDefaultValue) {
                  const res = findDataItem(
                    modalRef.datasource,
                    globalDefaultField,
                    globalDefaultValue,
                    null,
                  );
                  if (res) {
                    modalRef.selectedRows = res;
                  }
                }
              }
            }
          }
          emit('update:label', getLabelFromDatasource(modalRef.datasource));
        });
      }
    };
    const findDataItem = (treeData: any, key: string, value: any, parent: any) => {
      for (const item of treeData) {
        if (item[key] === value) {
          return [item[key]];
        }
        if (item.children) {
          let res = findDataItem(item.children, key, value, item);
          if (res) {
            res = [item[key], ...res];
            if (parent) {
              res = [parent[key], ...res];
            }
            return res;
          }
        }
      }
      return null;
    };
    const getFirstData = (treeData: any, result: any[]) => {
      const firstData = treeData[0];
      result.push(firstData.value);
      if (firstData.children) {
        getFirstData(firstData.children, result);
      }
    };
    const debounce1: any = debounce(() => {
      refresh();
    }, 1000);
    const handleMouseenter = () => {
      if (is_refresh) {
        is_refresh = false;
        refresh();
      }
    };
    watch(
      // outModel 参与 searchType===1 的取值（见 resolveModelValue），必须一并监听：
      // 否则外部模型变化时查询条件不会重建。下方 JSON.stringify(searchConfig) 比对会挡住
      // 参数未变化时的重复刷新，不会多打请求。
      () => [props.formModel, props.outModel],
      () => {
        const oldSearch = JSON.stringify(searchConfig);
        if (props.field.queryType === 'simple') {
          searchConfig.simpleSearchParams = getSimpleSearchParams();
        } else {
          searchConfig.conditions = getConditions();
        }
        if (props.init_watch) {
          Object.keys(props.init_watch).forEach((key: any) => {
            searchConfig[props.init_watch[key]] = props.formModel[key];
          });
        }
        if (oldSearch == JSON.stringify(searchConfig)) {
          return;
        } else {
          debounce1();
        }
      },
      { deep: true },
    );
    watch(
      () => [
        props.field.searchConditions,
        props.field.queryType,
        props.field.simpleSearchConditions,
      ],
      () => {
        if (props.field.queryType === 'simple') {
          searchConfig.simpleSearchParams = getSimpleSearchParams();
        } else {
          searchConfig.conditions = getConditions();
        }
        is_refresh = true;
      },
      { deep: true },
    );
    watch(
      () => [
        props.labelKeyList,
        props.labelKey,
        props.labelKeyType,
        props.valueKey,
        props.labelSpace,
      ],
      () => {
        getTreeData(modalRef.baseData);
      },
      { deep: true },
    );
    watch(
      () => props.field.multiple,
      () => {
        modalRef.selectedRows = [];
        emit('update:value', modalRef.selectedRows);
      },
    );
    watch(
      () => props.field.postUrl,
      () => {
        is_refresh = true;
      },
    );
    const handleRequiredConditionChange = (key: string) => {
      if (props.field.required_condition) {
        const item = props.field.required_condition.find((item: any) => item.key === key);
        if (item) {
          debounce1();
        }
      }
    };
    onMounted(() => {
      required_condition_event_obj().on('required_condition_change', handleRequiredConditionChange);
      refresh();
    });
    onUnmounted(() => {
      required_condition_event_obj().off(
        'required_condition_change',
        handleRequiredConditionChange,
      );
    });
    return {
      props,
      modalRef,
      handleChange,
      handleMouseenter,
    };
  },

  data() {
    return {
      model: this.value,
    };
  },
});
</script>
